import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Modal } from '@/components/Modal';
import { Alert } from '@/components/Alert';
import { Select } from '@/components/Select';
import { Switch } from '@/components/Switch';
import { opportunityApi, opportunityErrors } from '@/pages/Opportunities/api';
import { opportunityFormSchema, initialOpportunityValues, opportunityPayload, type OpportunityFormValues } from '@/pages/Opportunities/formSchema';
import { opportunityFormSections } from '@/pages/Opportunities/formFields';
import { OpportunityFormField } from '@/pages/Opportunities/OpportunityFormField';
import { OpportunityClientField } from '@/pages/Opportunities/OpportunityClientField';
import { OpportunityClassifications } from '@/pages/Opportunities/OpportunityClassifications';
import type { OpportunityCatalogs, OpportunityDetail } from '@/pages/Opportunities/types';

export interface OpportunityFormProps { item: OpportunityDetail | null; date: string; catalogs: OpportunityCatalogs; onClose: () => void; onSaved: (item: OpportunityDetail) => void; }
export const OpportunityForm = ({ item, date, catalogs, onClose, onSaved }: OpportunityFormProps) => {
    const { t } = useTranslation('global');
    const [error, setError] = useState('');
    const form = useForm<OpportunityFormValues>({ defaultValues: initialOpportunityValues(item, date) });
    const submit = form.handleSubmit(async values => {
        setError(''); form.clearErrors();
        const parsed = opportunityFormSchema.safeParse(values);
        if(!parsed.success){
            for(const issue of parsed.error.issues){
                form.setError(issue.path.join('.') as keyof OpportunityFormValues, { message: t(issue.message.startsWith('opportunities.') ? issue.message : 'opportunities.required') });
            }
            setError(t('opportunities.check_fields'));
            return;
        }
        try{
            onSaved(await opportunityApi.save(item?.id ?? null, opportunityPayload(parsed.data, item?.record_version ?? 0)));
        }
        catch(failure){
            const errors = opportunityErrors(failure);
            setError(Object.values(errors).join(' '));
            for(const [field, message] of Object.entries(errors)){
                if(field in values){
                    form.setError(field as keyof OpportunityFormValues, { message });
                }
            }
        }
    });
    return <Modal isOpen onClose={onClose} title={t(item ? 'opportunities.edit' : 'opportunities.create')} maxWidth="2xl" onSubmit={submit} isSubmitting={form.formState.isSubmitting}
        hasUnsavedChanges={form.formState.isDirty} submitLabel={t('opportunities.save')}>
        <div className="space-y-7 text-slate-900 dark:text-zinc-100">
            {error && <Alert variant="error">{error}</Alert>}
            {item && <p className="font-mono">{item.code}</p>}
            <OpportunityClientField form={form} selected={item?.client} />
            <Select label={t('opportunities.fields.procurement_type')} required value={form.watch('procurement_type')} onChange={(value: 'public' | 'private') => form.setValue('procurement_type', value, { shouldDirty: true })}
                error={form.formState.errors.procurement_type?.message}
                options={(['public', 'private'] as const).map(type => ({ value: type, label: t(`opportunities.type.${type}`) }))} />
            {opportunityFormSections.map(section => <section key={section.key} className="space-y-4">
                <h3 className="border-b border-slate-200 pb-2 font-semibold dark:border-white/15">{t(`opportunities.sections.${section.key}`)}</h3>
                {section.fields.map(field => <OpportunityFormField key={field.name} field={field} form={form} catalogs={catalogs} />)}
            </section>)}
            <Switch label={t('opportunities.fields.is_ute')} checked={form.watch('is_ute')} onChange={value => form.setValue('is_ute', value, { shouldDirty: true })} />
            <Switch label={t('opportunities.fields.is_closed_price')} checked={form.watch('is_closed_price')} onChange={value => form.setValue('is_closed_price', value, { shouldDirty: true })} />
            <OpportunityClassifications form={form} catalogs={catalogs} />
            <Switch label={t('opportunities.past_confirmation')} checked={form.watch('past_deadline_confirmed')} onChange={value => form.setValue('past_deadline_confirmed', value, { shouldDirty: true })} />
        </div>
    </Modal>;
};
